/**
 * Module 118 (WP-118.2) — the daily reward's 7-day streak, pure.
 *
 * Days are UTC `YYYY-MM-DD` strings from the Daily Challenge's helper
 * (`dailyDate(clock)`), so both daily features turn over at the same instant
 * for every player. The rules (CLAUDE.md D3 / D10):
 *
 * - One claim per UTC day. Claiming the day after the last claim continues the
 *   streak; anything else starts again at day 1. After day 7 comes day 1.
 * - Missing EXACTLY one day offers a repair (keep the streak for
 *   `STREAK_REPAIR_PRICE` coins), once per missed day. Two or more missed days:
 *   no offer. A repair after day 7 would keep nothing (the cycle restarts at
 *   day 1 anyway), so day 7 is never offered one.
 *
 * The plugin owns the effects (credit, debit, ticket, save, analytics); this
 * file only answers questions and returns new state.
 */
import { DAILY_LOGIN_SCHEDULE, DAILY_LOGIN_TICKET_DAY } from '../constants/economy';
import { utcYesterday } from '../daily/daily-spec';
export const EMPTY_DAILY_LOGIN = { day: 0 };
const CYCLE = DAILY_LOGIN_SCHEDULE.length;
export function claimableToday(state, today) {
    return state.lastClaimUtc !== today;
}
/** True when exactly one UTC day was missed since the last claim. */
function missedOneDay(state, today) {
    return (state.lastClaimUtc !== undefined && state.lastClaimUtc === utcYesterday(utcYesterday(today)));
}
/**
 * The streak day today's claim would be (1–7). Equals the day that is offered
 * even when a repair is on the table: until the player pays, the card shows
 * the start-over day.
 */
export function nextStreakDay(state, today) {
    return continuesStreak(state, today) ? (state.day % CYCLE) + 1 : 1;
}
/**
 * Today's claim carries the streak on (from yesterday, or over a repaired
 * day). Module 132 counts consecutive days with it, past the 7-day cycle.
 */
export function continuesStreak(state, today) {
    return (state.lastClaimUtc === utcYesterday(today) ||
        (state.repairedUtc === today && missedOneDay(state, today)));
}
/** The streak the player can still buy back today, or null. `streakDay` is what is kept. */
export function repairOffer(state, today) {
    if (!claimableToday(state, today))
        return null;
    if (state.repairedUtc === today)
        return null;
    if (!missedOneDay(state, today))
        return null;
    if (state.day < 1 || state.day >= CYCLE)
        return null;
    return { streakDay: state.day };
}
/** The state after buying the repair, or null when no repair is offered. */
export function repair(state, today) {
    if (!repairOffer(state, today))
        return null;
    return { ...state, repairedUtc: today };
}
/** Today's claim, or null when today is already claimed. */
export function claim(state, today) {
    if (!claimableToday(state, today))
        return null;
    const streakDay = nextStreakDay(state, today);
    return {
        // `repairedUtc` is dropped: it has done its job, and a stale one can never
        // matter again (it counts only on the day it was bought).
        state: { day: streakDay, lastClaimUtc: today },
        streakDay,
        coins: DAILY_LOGIN_SCHEDULE[streakDay - 1],
        ticket: streakDay === DAILY_LOGIN_TICKET_DAY,
    };
}
